import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { BookOpen, LayoutDashboard, Users, Inbox, BarChart3, Settings, LogOut, Copy, Check, Library, PenTool, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function TeacherLayout() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate('/login');
            return;
        }
        if (user.role !== 'teacher' && user.role !== 'admin') {
            navigate('/dashboard');
            return;
        }
        api.get('/api/teacher/me')
            .then((r) => setProfile(r.data))
            .catch((e) => {
                if (e?.response?.status === 402) {
                    navigate('/pricing');
                }
            });
    }, [user, loading, navigate]);

    const copyCode = () => {
        if (!profile?.teacher_code) return;
        navigator.clipboard.writeText(profile.teacher_code);
        setCopied(true);
        toast.success('Teacher code copied');
        setTimeout(() => setCopied(false), 1500);
    };

    const linkClass = ({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${
            isActive
                ? 'bg-[#7C3AED] text-white shadow-sm'
                : 'text-zinc-600 hover:bg-[#EDE4FF] hover:text-[#7C3AED]'
        }`;

    return (
        <div className="min-h-screen bg-[#F8F7FC] flex flex-col lg:flex-row" data-testid="teacher-layout">
            {/* Sidebar */}
            <aside className="lg:w-72 bg-white border-b-2 lg:border-b-0 lg:border-r-2 border-zinc-950 flex flex-col">
                <div className="px-5 py-5 border-b border-zinc-200">
                    <a href="/" className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#A78BFA] border-2 border-zinc-950 flex items-center justify-center">
                            <BookOpen className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <div className="text-sm font-black tracking-tight">A-Level AI Grader</div>
                            <div className="text-[10px] uppercase tracking-[0.14em] text-[#7C3AED] font-bold">Teacher</div>
                        </div>
                    </a>
                </div>

                {profile?.teacher_code && (
                    <button
                        onClick={copyCode}
                        className="mx-4 mt-4 px-3 py-2.5 rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] text-white text-left transition-transform hover:scale-[1.02]"
                        data-testid="teacher-code-pill"
                    >
                        <div className="text-[10px] font-bold uppercase tracking-wider text-white/70">Your Teacher Code</div>
                        <div className="flex items-center justify-between mt-0.5">
                            <span className="font-black text-lg tracking-wide">{profile.teacher_code}</span>
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4 opacity-80" />}
                        </div>
                    </button>
                )}

                <nav className="flex-1 px-4 py-4 space-y-1.5">
                    <NavLink to="/teacher" end className={linkClass} data-testid="nav-overview">
                        <LayoutDashboard className="w-4 h-4" /> Overview
                    </NavLink>
                    <NavLink to="/teacher/classes" className={linkClass} data-testid="nav-classes">
                        <Users className="w-4 h-4" /> My Classes
                    </NavLink>
                    <NavLink to="/teacher/submissions" className={linkClass} data-testid="nav-submissions">
                        <Inbox className="w-4 h-4" /> Submissions
                    </NavLink>
                    <NavLink to="/teacher/mark" className={linkClass} data-testid="nav-mark">
                        <ClipboardCheck className="w-4 h-4" /> Mark
                    </NavLink>
                    <NavLink to="/teacher/question-bank" className={linkClass} data-testid="nav-question-bank">
                        <Library className="w-4 h-4" /> Question Bank
                    </NavLink>
                    <NavLink to="/teacher/create" className={linkClass} data-testid="nav-create">
                        <PenTool className="w-4 h-4" /> Create
                    </NavLink>
                    <NavLink to="/teacher/analytics" className={linkClass} data-testid="nav-analytics">
                        <BarChart3 className="w-4 h-4" /> Analytics
                    </NavLink>
                    <NavLink to="/teacher/settings" className={linkClass} data-testid="nav-settings">
                        <Settings className="w-4 h-4" /> Settings
                    </NavLink>
                </nav>

                <div className="px-4 py-4 border-t border-zinc-200">
                    <div className="text-sm font-bold mb-1 truncate">{user?.name}</div>
                    <div className="text-xs text-zinc-500 mb-3 truncate">{profile?.school || user?.email}</div>
                    {user?.role === 'admin' && (
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="w-full inline-flex items-center justify-center gap-2 py-2 mb-2 rounded-lg bg-white border-2 border-[#7C3AED] text-[#7C3AED] text-xs font-bold hover:bg-[#F4ECFF]"
                            data-testid="switch-to-student"
                        >
                            ← Student view
                        </button>
                    )}
                    <button
                        onClick={async () => { await logout(); navigate('/login'); }}
                        className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg border border-zinc-200 text-xs font-bold text-zinc-700 hover:bg-zinc-100"
                        data-testid="teacher-logout"
                    >
                        <LogOut className="w-3.5 h-3.5" /> Log out
                    </button>
                </div>
            </aside>

            <main className="flex-1 p-4 sm:p-8 max-w-full overflow-x-hidden">
                <Outlet context={{ profile, refreshProfile: () => api.get('/api/teacher/me').then(r => setProfile(r.data)) }} />
            </main>
        </div>
    );
}
